import React, { useContext, useRef, useState } from 'react';
import { Image } from 'shared';
import ReactQuill from 'react-quill';

import { media } from 'context';
import Button from 'ds/Button';
import Layout from 'ds/Layout';
import Lightbox from 'ds/Lightbox';
import MessageImageUploader from 'ds/MessageThread/MessageImageUploader';
import MessagePdfUploader from 'ds/MessageThread/MessagePdfUploader';
import { CreatableMessage } from 'ds/MessageThread/types';
import RichTextEditor from 'ds/RichTextEditor';
import TaskImageThumbnail from 'ds/TaskImageThumbnail';
import useKeyDown from 'ds/helpers/useKeyDown';
import useKeyUp from 'ds/helpers/useKeyUp';
import { RequestState } from 'ds/types';

interface Props {
  onSubmit: (message: CreatableMessage) => Promise<void>;
}

const NEW_MESSAGE: CreatableMessage = {
  body: '<p><br></p>',
  images: [],
  files: []
};

const keyMap: Record<string, boolean> = {};

const MessageThreadInput: React.FC<Props> = ({ onSubmit }) => {
  const [requestState, setRequestState] = useState<RequestState>('ready');
  const [newMessage, setNewMessage] = useState<CreatableMessage>(NEW_MESSAGE);
  const ref = useRef<ReactQuill>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [lightboxStartingIndex, setLightboxStartingIndex] = useState<number>(-1);
  const { xs } = useContext(media);
  const requestInProgress = requestState === 'in_progress';
  const { body, images = [], files = [] } = newMessage;
  const isValid = !!body && body.replace(/<p><br><\/p>/g, '').trim().length > 0;
  const imagesOrEmptyArray = images;
  const filesOrEmptyArray = files;

  useKeyDown((e: KeyboardEvent) => {
    if (!newMessage.body || !ref.current?.editor?.hasFocus()) return;

    keyMap[e.key] = true;

    if (keyMap['Enter'] && keyMap['Meta']) {
      handleSubmit();
    } else if (keyMap['Enter']) {
      setRequestState('ready');
    }
  });

  const handleSubmit = async () => {
    if (requestInProgress) return;

    const messageBody = newMessage.body.replace(/<p><br><\/p>$/, '');

    if (!messageBody.trim().length) return;

    setRequestState('in_progress');

    await onSubmit({ body: messageBody, images, files: filesOrEmptyArray });

    setNewMessage(NEW_MESSAGE);

    setRequestState('ready');

    // Scroll to bottom after message update
    window.scrollTo({
      top: document.body.scrollHeight
    });
  };

  useKeyUp((e: KeyboardEvent) => {
    keyMap[e.key] = false;
  });

  const imagesToShow = images.filter(i => typeof i.id === 'number' && !!i.key) as Image[];

  return (
    <Layout>
      {lightboxStartingIndex >= 0 && (
        <Lightbox
          images={imagesToShow}
          startingIndex={lightboxStartingIndex}
          onClose={() => setLightboxStartingIndex(-1)}
          padResize
        />
      )}
      {!!imagesToShow?.length && (
        <Layout flex wrap position="relative" left={-6} width="calc(100% + 12px)">
          {imagesToShow.map(({ key }, index) => {
            return (
              <TaskImageThumbnail
                onClick={() => setLightboxStartingIndex(index)}
                style={{
                  padding: xs ? '10px 6px' : '8px 6px',
                  width: `calc(${100 / (xs ? 3 : 6)}%)`
                }}
                key={key}
                srcKey={key || ''}
              />
            );
          })}
        </Layout>
      )}
      <RichTextEditor
        value={body}
        modules={isFocused ? undefined : { toolbar: false }}
        onChange={value => {
          setNewMessage({ ...newMessage, body: value });
        }}
        key={isFocused ? 'focused' : 'not-focused'}
        placeholder="Add to the conversation..."
        readOnly={requestInProgress}
        ref={ref}
        onFocus={() => {
          setIsFocused(true);
          setTimeout(() => {
            ref.current?.focus();
          }, 100);
        }}
        onBlur={() => setIsFocused(false)}
      />
      <Layout
        marginTop={8}
        {...(imagesOrEmptyArray.length > 0 || filesOrEmptyArray.length > 0
          ? {}
          : { direction: 'row', align: 'center', justify: 'space-between' })}
      >
        <Layout
          {...(imagesOrEmptyArray.length > 0 || filesOrEmptyArray.length > 0
            ? { direction: 'column', align: 'flex-start' }
            : { direction: 'row', align: 'center' })}
        >
          <MessageImageUploader
            images={imagesOrEmptyArray}
            label="Attach image"
            onChange={uploadedFiles => {
              setNewMessage({
                ...newMessage,
                images: [
                  ...imagesOrEmptyArray,
                  ...uploadedFiles.map(file => {
                    return { url: URL.createObjectURL(file), file };
                  })
                ]
              });
            }}
            onDeleteClick={image => {
              setNewMessage({ ...newMessage, images: imagesOrEmptyArray.filter(i => i.url !== image.url) });
            }}
            disabled={requestInProgress}
          />
          <Layout
            {...(imagesOrEmptyArray.length > 0 || filesOrEmptyArray.length > 0
              ? { marginTop: 16 }
              : { marginLeft: 16 })}
          >
            <MessagePdfUploader
              files={filesOrEmptyArray}
              label="Attach PDF"
              onChange={uploadedFiles => {
                setNewMessage({
                  ...newMessage,
                  files: [
                    ...filesOrEmptyArray,
                    ...uploadedFiles.map(file => {
                      return {
                        url: URL.createObjectURL(file),
                        file,
                        filename: file.name,
                        filesize: file.size
                      };
                    })
                  ]
                });
              }}
              onDeleteClick={file => {
                setNewMessage({ ...newMessage, files: filesOrEmptyArray.filter(f => f.url !== file.url) });
              }}
              disabled={requestInProgress}
            />
          </Layout>
        </Layout>
        <Layout minWidth={120} justify="flex-end">
          <Button
            type="primary"
            size="sm"
            text="Comment"
            onClick={handleSubmit}
            disabled={!isValid || requestInProgress}
            showSpinner={requestInProgress}
          />
        </Layout>
      </Layout>
    </Layout>
  );
};

export default MessageThreadInput;
